import { createTheme } from "@mui/material";

// Palette Configuration
const palette = {
  primary: {
    main: "#f5a623", // Orange
    contrastText: "#4B4B4D", // White
  },
  secondary: {
    main: "#4272b7", // Blue
    contrastText: "#ffffff",
  },
  neutral: {
    50: "#fefefe",
    100: "#fafafc",
    200: "#f8f8fb",
    300: "#f5f5f9",
    400: "#f3f3f8",
    500: "#f0f0f6",
    600: "#dadae0",
    700: "#aaaaaf",
    800: "#848487",
    900: "#656567",
    1000: "#4B4B4D",
    main: "#b0b0b0",
    contrastText: "#4B4B4D",
  },
  error: {
    main: "#e55c5c",
    contrastText: "#ffffff",
  },
  warning: {
    main: "#dec159",
    contrastText: "#ffffff",
  },
  success: {
    main: "#57d983",
    contrastText: "#ffffff",
  },
  info: {
    main: "#5996de",
    contrastText: "#ffffff",
  },
};

// Button Style Generator
const generateButtonStyles = (variant, color, size) => {
  const sizes = {
    large: {
      height: "48px",
      fontSize: "17.5px",
      lineHeight: "26px",
      padding: "8px 16px",
    },
    medium: {
      height: "40px",
      fontSize: "16px",
      lineHeight: "24px",
      padding: "8px 16px",
    },
    small: {
      height: "32px",
      fontSize: "14px",
      lineHeight: "22px",
      padding: "4px 12px",
    },
  };

  const baseStyles = {
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "12px",
    boxShadow: "none",
    letterSpacing: "0.46px",
  };

  if (variant === "contained") {
    return {
      ...baseStyles,
      backgroundColor: palette[color]?.main || palette.primary.main,
      color: palette[color]?.contrastText || palette.neutral.contrastText,
      "&:hover": {
        backgroundColor: palette[color]?.[600] || palette.primary.dark,
      },
      "&.Mui-disabled": {
        backgroundColor: palette.neutral[200],
        color: palette.neutral[600],
      },
      ...sizes[size],
    };
  }

  if (variant === "outlined") {
    return {
      ...baseStyles,
      backgroundColor: "transparent",
      border: `1px solid ${palette[color]?.main || palette.primary.main}`,
      color: palette[color]?.main || palette.primary.main,
      "&:hover": {
        backgroundColor: palette[color]?.[200] || palette.neutral[200],
      },
      "&.Mui-disabled": {
        borderColor: palette.neutral[300],
        color: palette.neutral[600],
      },
      ...sizes[size],
    };
  }

  if (variant === "text") {
    return {
      ...baseStyles,
      backgroundColor: "transparent",
      color:
        color === "neutral"
          ? palette.neutral.contrastText
          : palette[color]?.main,
      "&:hover": {
        backgroundColor: palette[color]?.[200] || palette.neutral[200],
      },
      "&.Mui-disabled": {
        color: palette.neutral[600],
      },
      ...sizes[size],
    };
  }

  return baseStyles;
};

// Light Theme
const LightTheme = createTheme({
  spacing: 8,
  breakpoints: {
    values: {
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
      "2xl": 1536,
    },
  },
  palette,
  typography: {
    fontFamily: "Hanken Grotesk, Poppins, Open Sans, sans-serif",

    h1: {
      fontFamily: "Poppins, serif",
      fontSize: "80px",
      fontWeight: 800,
      lineHeight: "100px", // 1.25
    },
    h2: {
      fontFamily: "Poppins, serif",
      fontSize: "60px",
      fontWeight: 800,
      lineHeight: "80px", // 1.33
    },
    h3: {
      fontFamily: "Poppins, serif",
      fontSize: "40px",
      fontWeight: 700,
      lineHeight: "60px", // 1.5
    },
    h4: {
      fontFamily: "Poppins, serif",
      fontSize: "30px",
      fontWeight: 700,
      lineHeight: "45px", // 1.5
    },
    h5: {
      fontFamily: "Poppins, serif",
      fontSize: "23.75px",
      fontWeight: 700,
      lineHeight: "36px", // 1.52
    },
    h6: {
      fontFamily: "Poppins, serif",
      fontSize: "22.5px",
      fontWeight: 600,
      lineHeight: "36px", // 1.59
    },

    subtitle1: {
      fontSize: "20px",
      fontWeight: 500,
      lineHeight: "30px", // 1.5
    },
    subtitle1_bold: {
      fontSize: "20px",
      fontWeight: 600,
      lineHeight: "30px", // 1.5
    },
    subtitle2: {
      fontSize: "17.5px",
      fontWeight: 500,
      lineHeight: "28px", // 1.59
    },
    subtitle2_bold: {
      fontSize: "17.5px",
      fontWeight: 600,
      lineHeight: "28px", // 1.59
    },

    body1: {
      fontSize: "20px",
      fontWeight: 400,
      lineHeight: "30px", // 1.5
    },
    body2: {
      fontSize: "17.5px",
      fontWeight: 400,
      lineHeight: "28px", // 1.59
    },

    caption: {
      fontSize: "15px",
      fontWeight: 400,
      lineHeight: "22px", // 1.47
    },
    overline: {
      fontSize: "15px",
      fontWeight: 700,
      lineHeight: "22px", // 1.47
      textTransform: "uppercase",
    },
    button: {
      fontSize: "17.5px",
      fontWeight: 700,
      lineHeight: "31px", // 1.76
      textTransform: "none",
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        variant: "contained",
        // color: "primary",
        disableRipple: true,
      },
      variants: [
        {
          props: { variant: "contained", color: "primary", size: "large" },
          style: generateButtonStyles("contained", "primary", "large"),
        },
        {
          props: { variant: "contained", color: "primary", size: "medium" },
          style: generateButtonStyles("contained", "primary", "medium"),
        },
        {
          props: { variant: "contained", color: "primary", size: "small" },
          style: generateButtonStyles("contained", "primary", "small"),
        },
        {
          props: { variant: "contained", color: "secondary", size: "large" },
          style: generateButtonStyles("contained", "secondary", "large"),
        },
        {
          props: { variant: "contained", color: "secondary", size: "medium" },
          style: generateButtonStyles("contained", "secondary", "medium"),
        },
        {
          props: { variant: "contained", color: "secondary", size: "small" },
          style: generateButtonStyles("contained", "secondary", "small"),
        },
        {
          props: { variant: "contained", color: "error", size: "large" },
          style: generateButtonStyles("contained", "error", "large"),
        },
        {
          props: { variant: "contained", color: "error", size: "medium" },
          style: generateButtonStyles("contained", "error", "medium"),
        },
        {
          props: { variant: "contained", color: "error", size: "small" },
          style: generateButtonStyles("contained", "error", "small"),
        },
        {
          props: { variant: "contained", color: "info", size: "large" },
          style: generateButtonStyles("contained", "info", "large"),
        },
        {
          props: { variant: "contained", color: "info", size: "medium" },
          style: generateButtonStyles("contained", "info", "medium"),
        },
        {
          props: { variant: "contained", color: "info", size: "small" },
          style: generateButtonStyles("contained", "info", "small"),
        },
        {
          props: { variant: "contained", color: "success", size: "large" },
          style: generateButtonStyles("contained", "success", "large"),
        },
        {
          props: { variant: "contained", color: "success", size: "medium" },
          style: generateButtonStyles("contained", "success", "medium"),
        },
        {
          props: { variant: "contained", color: "success", size: "small" },
          style: generateButtonStyles("contained", "success", "small"),
        },
        {
          props: { variant: "contained", color: "warning", size: "large" },
          style: generateButtonStyles("contained", "warning", "large"),
        },
        {
          props: { variant: "contained", color: "warning", size: "medium" },
          style: generateButtonStyles("contained", "warning", "medium"),
        },
        {
          props: { variant: "contained", color: "warning", size: "small" },
          style: generateButtonStyles("contained", "warning", "small"),
        },
        {
          props: { variant: "contained", color: "neutral", size: "large" },
          style: generateButtonStyles("contained", "neutral", "large"),
        },
        {
          props: { variant: "contained", color: "neutral", size: "medium" },
          style: generateButtonStyles("contained", "neutral", "medium"),
        },
        {
          props: { variant: "contained", color: "neutral", size: "small" },
          style: generateButtonStyles("contained", "neutral", "small"),
        },
        // outlined
        {
          props: { variant: "outlined", color: "primary", size: "large" },
          style: generateButtonStyles("outlined", "primary", "large"),
        },
        {
          props: { variant: "outlined", color: "primary", size: "medium" },
          style: generateButtonStyles("outlined", "primary", "medium"),
        },
        {
          props: { variant: "outlined", color: "primary", size: "small" },
          style: generateButtonStyles("outlined", "primary", "small"),
        },
        {
          props: { variant: "outlined", color: "secondary", size: "large" },
          style: generateButtonStyles("outlined", "secondary", "large"),
        },
        {
          props: { variant: "outlined", color: "secondary", size: "medium" },
          style: generateButtonStyles("outlined", "secondary", "medium"),
        },
        {
          props: { variant: "outlined", color: "secondary", size: "small" },
          style: generateButtonStyles("outlined", "secondary", "small"),
        },
        {
          props: { variant: "outlined", color: "error", size: "large" },
          style: generateButtonStyles("outlined", "error", "large"),
        },
        {
          props: { variant: "outlined", color: "error", size: "medium" },
          style: generateButtonStyles("outlined", "error", "medium"),
        },
        {
          props: { variant: "outlined", color: "error", size: "small" },
          style: generateButtonStyles("outlined", "error", "small"),
        },
        {
          props: { variant: "outlined", color: "info", size: "large" },
          style: generateButtonStyles("outlined", "info", "large"),
        },
        {
          props: { variant: "outlined", color: "info", size: "medium" },
          style: generateButtonStyles("outlined", "info", "medium"),
        },
        {
          props: { variant: "outlined", color: "info", size: "small" },
          style: generateButtonStyles("outlined", "info", "small"),
        },
        {
          props: { variant: "outlined", color: "success", size: "large" },
          style: generateButtonStyles("outlined", "success", "large"),
        },
        {
          props: { variant: "outlined", color: "success", size: "medium" },
          style: generateButtonStyles("outlined", "success", "medium"),
        },
        {
          props: { variant: "outlined", color: "success", size: "small" },
          style: generateButtonStyles("outlined", "success", "small"),
        },
        {
          props: { variant: "outlined", color: "warning", size: "large" },
          style: generateButtonStyles("outlined", "warning", "large"),
        },
        {
          props: { variant: "outlined", color: "warning", size: "medium" },
          style: generateButtonStyles("outlined", "warning", "medium"),
        },
        {
          props: { variant: "outlined", color: "warning", size: "small" },
          style: generateButtonStyles("outlined", "warning", "small"),
        },

        {
          props: { variant: "outlined", color: "neutral", size: "large" },
          style: generateButtonStyles("outlined", "neutral", "large"),
        },
        {
          props: { variant: "outlined", color: "neutral", size: "medium" },
          style: generateButtonStyles("outlined", "neutral", "medium"),
        },
        {
          props: { variant: "outlined", color: "neutral", size: "small" },
          style: generateButtonStyles("outlined", "neutral", "small"),
        },
        // text
        {
          props: { variant: "text", color: "primary", size: "large" },
          style: generateButtonStyles("text", "primary", "large"),
        },
        {
          props: { variant: "text", color: "primary", size: "medium" },
          style: generateButtonStyles("text", "primary", "medium"),
        },
        {
          props: { variant: "text", color: "primary", size: "small" },
          style: generateButtonStyles("text", "primary", "small"),
        },
        {
          props: { variant: "text", color: "secondary", size: "large" },
          style: generateButtonStyles("text", "secondary", "large"),
        },
        {
          props: { variant: "text", color: "secondary", size: "medium" },
          style: generateButtonStyles("text", "secondary", "medium"),
        },
        {
          props: { variant: "text", color: "secondary", size: "small" },
          style: generateButtonStyles("text", "secondary", "small"),
        },
        {
          props: { variant: "text", color: "error", size: "large" },
          style: generateButtonStyles("text", "error", "large"),
        },
        {
          props: { variant: "text", color: "error", size: "medium" },
          style: generateButtonStyles("text", "error", "medium"),
        },
        {
          props: { variant: "text", color: "error", size: "small" },
          style: generateButtonStyles("text", "error", "small"),
        },
        {
          props: { variant: "text", color: "info", size: "large" },
          style: generateButtonStyles("text", "info", "large"),
        },
        {
          props: { variant: "text", color: "info", size: "medium" },
          style: generateButtonStyles("text", "info", "medium"),
        },
        {
          props: { variant: "text", color: "info", size: "small" },
          style: generateButtonStyles("text", "info", "small"),
        },
        {
          props: { variant: "text", color: "success", size: "large" },
          style: generateButtonStyles("text", "success", "large"),
        },
        {
          props: { variant: "text", color: "success", size: "medium" },
          style: generateButtonStyles("text", "success", "medium"),
        },
        {
          props: { variant: "text", color: "success", size: "small" },
          style: generateButtonStyles("text", "success", "small"),
        },
        {
          props: { variant: "text", color: "warning", size: "large" },
          style: generateButtonStyles("text", "warning", "large"),
        },
        {
          props: { variant: "text", color: "warning", size: "medium" },
          style: generateButtonStyles("text", "warning", "medium"),
        },
        {
          props: { variant: "text", color: "warning", size: "small" },
          style: generateButtonStyles("text", "warning", "small"),
        },
        {
          props: { variant: "text", color: "neutral", size: "large" },
          style: generateButtonStyles("text", "neutral", "large"),
        },
        {
          props: { variant: "text", color: "neutral", size: "medium" },
          style: generateButtonStyles("text", "neutral", "medium"),
        },
        {
          props: { variant: "text", color: "neutral", size: "small" },
          style: generateButtonStyles("text", "neutral", "small"),
        },
      ],
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            "& fieldset": { borderColor: palette.neutral[700] },
            "&:hover fieldset": { borderColor: palette.primary.main },
            "&.Mui-focused fieldset": { borderColor: palette.primary.main },
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          "& fieldset": { borderColor: palette.neutral[700] },
          "&:hover fieldset": { borderColor: palette.primary.main },
          "&:hover": { borderColor: palette.primary.main },
          "&.Mui-focused fieldset": { borderColor: palette.primary.main },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          "& fieldset": { borderColor: palette.neutral[700] },
          "&:hover fieldset": { borderColor: palette.primary.main },
          "&:hover": { borderColor: palette.primary.main },
          "&.Mui-focused fieldset": { borderColor: palette.primary.main },
        },
      },
    },
    // MuiInputBase: {
    //   styleOverrides: {
    //     root: {
    //       borderRadius: "8px",
    //       "& fieldset": { borderColor: palette.neutral[700] },
    //       "&:hover fieldset": { borderColor: palette.primary.main },
    //       "&:hover": { borderColor: palette.primary.main },
    //       "&.Mui-focused fieldset": { borderColor: palette.primary.main },
    //     },
    //   },
    // },

    MuiCard: {
      defaultProps: {
        elevation: 4,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 16,
          padding: theme.spacing(3),
          "& .MuiCardContent-root": {
            padding: 0, // wipe all inner padding
            "&:last-child": { paddingBottom: 0 },
          },
        }),
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 0,
          "&:last-child": { paddingBottom: 0 },
        },
      },
    },
  },
});

// Dark Theme
const DarkTheme = createTheme({
  ...LightTheme,
  palette: {
    ...palette,
    background: {
      default: "#121212",
      paper: "#1E1E1E",
    },
    text: {
      primary: "#ffffff",
      secondary: "#aaaaaa",
    },
  },
});

export { LightTheme, DarkTheme };
